/**
 * Live Quiz Play Page
 *
 * Page component for joining and playing a real-time interactive live quiz.
 * Handles the join flow (PIN + nickname) and renders the PlayerView.
 */

import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PlayerView } from '../components/live-quiz/player/PlayerView';
import type { JoinSessionResponse } from '../types/liveQuiz';

type PageState = 'join' | 'joining' | 'playing' | 'kicked' | 'error';

// Avatar options
const AVATARS = [
  { id: 'fox', emoji: '🦊' },
  { id: 'cat', emoji: '🐱' },
  { id: 'dog', emoji: '🐶' },
  { id: 'bear', emoji: '🐻' },
  { id: 'panda', emoji: '🐼' },
  { id: 'koala', emoji: '🐨' },
  { id: 'lion', emoji: '🦁' },
  { id: 'tiger', emoji: '🐯' },
  { id: 'rabbit', emoji: '🐰' },
  { id: 'frog', emoji: '🐸' },
  { id: 'unicorn', emoji: '🦄' },
  { id: 'alien', emoji: '👽' },
];

export function LiveQuizPlayPage() {
  const { t, i18n } = useTranslation('liveQuiz');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isRTL = i18n.dir() === 'rtl';

  // Pre-fill PIN from URL if provided
  const initialPin = searchParams.get('pin') || '';

  const [pageState, setPageState] = useState<PageState>('join');
  const [gamePin, setGamePin] = useState(initialPin);
  const [nickname, setNickname] = useState('');
  const [selectedAvatar, setSelectedAvatar] = useState(AVATARS[0].id);
  const [error, setError] = useState<string | null>(null);
  const [kickedReason, setKickedReason] = useState<string | null>(null);

  // Session data after joining
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [participantId, setParticipantId] = useState<string | null>(null);

  // Handle join submission
  const handleJoin = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    if (!gamePin.trim()) {
      setError(t('player.join.errors.enterPin'));
      return;
    }

    if (!nickname.trim()) {
      setError(t('player.join.errors.enterNickname'));
      return;
    }

    if (nickname.length > 20) {
      setError(t('player.join.errors.nicknameTooLong'));
      return;
    }

    setPageState('joining');
    setError(null);

    try {
      const response = await fetch('/api/live-quiz/join', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          gamePin: gamePin.trim().toUpperCase(),
          nickname: nickname.trim(),
          avatar: selectedAvatar,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json() as { error?: string };
        throw new Error(errorData.error || 'Failed to join game');
      }

      const joinData = await response.json() as JoinSessionResponse;
      setSessionId(joinData.sessionId);
      setParticipantId(joinData.participantId);
      setPageState('playing');

      // Store participant info in sessionStorage for reconnection
      sessionStorage.setItem('liveQuizParticipant', JSON.stringify({
        sessionId: joinData.sessionId,
        participantId: joinData.participantId,
        nickname: nickname.trim(),
      }));
    } catch (err) {
      setError(err instanceof Error ? err.message : t('player.join.errors.failedToJoin'));
      setPageState('join');
    }
  }, [gamePin, nickname, selectedAvatar, t]);

  // Check for existing session on mount
  useEffect(() => {
    const stored = sessionStorage.getItem('liveQuizParticipant');
    if (stored) {
      try {
        JSON.parse(stored);
        // Could prompt user to rejoin existing session
        // For now, just clear it and start fresh
        // In production, you might want to check if the session is still active
      } catch {
        sessionStorage.removeItem('liveQuizParticipant');
      }
    }
  }, []);

  // Handle being kicked
  const handleKicked = useCallback((reason?: string) => {
    setKickedReason(reason || t('player.kicked.defaultReason'));
    setPageState('kicked');
    setSessionId(null);
    setParticipantId(null);
    sessionStorage.removeItem('liveQuizParticipant');
  }, [t]);

  // Handle leaving/playing again
  const handleLeave = useCallback(() => {
    setSessionId(null);
    setParticipantId(null);
    setPageState('join');
    setGamePin('');
    setNickname('');
    setError(null);
    setKickedReason(null);
    sessionStorage.removeItem('liveQuizParticipant');
  }, []);

  // Format PIN input (add visual spacing)
  const formatPin = (value: string) => {
    return value.replace(/\D/g, '').slice(0, 6).toUpperCase();
  };

  // Join form
  if (pageState === 'join' || pageState === 'joining') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-600 via-purple-700 to-indigo-800 flex items-center justify-center p-4" dir={isRTL ? 'rtl' : 'ltr'}>
        <div className="w-full max-w-md">
          {/* Logo/Title */}
          <div className="text-center mb-8">
            <div className="text-6xl mb-4">🎮</div>
            <h1 className="text-3xl font-bold text-white">{t('player.join.title')}</h1>
            <p className="text-purple-300 mt-2">{t('player.join.subtitle')}</p>
          </div>

          {/* Join Form */}
          <form onSubmit={handleJoin} className="space-y-6">
            {/* Game PIN */}
            <div>
              <label className="block text-sm font-medium text-purple-200 mb-2">
                {t('player.join.gamePin')}
              </label>
              <input
                type="text"
                value={gamePin}
                onChange={(e) => setGamePin(formatPin(e.target.value))}
                placeholder={t('player.join.pinPlaceholder')}
                className="w-full px-6 py-4 text-center text-3xl font-mono font-bold tracking-widest bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent"
                maxLength={6}
                disabled={pageState === 'joining'}
                autoFocus
              />
            </div>

            {/* Nickname */}
            <div>
              <label className="block text-sm font-medium text-purple-200 mb-2">
                {t('player.join.nickname')}
              </label>
              <input
                type="text"
                value={nickname}
                onChange={(e) => setNickname(e.target.value)}
                placeholder={t('player.join.nicknamePlaceholder')}
                className="w-full px-4 py-3 text-lg bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent"
                maxLength={20}
                disabled={pageState === 'joining'}
              />
            </div>

            {/* Avatar Selection */}
            <div>
              <label className="block text-sm font-medium text-purple-200 mb-2">
                {t('player.join.pickAvatar')}
              </label>
              <div className="grid grid-cols-6 gap-2">
                {AVATARS.map((avatar) => (
                  <button
                    key={avatar.id}
                    type="button"
                    onClick={() => setSelectedAvatar(avatar.id)}
                    className={`p-3 text-2xl rounded-lg transition-all ${
                      selectedAvatar === avatar.id
                        ? 'bg-purple-500 ring-2 ring-white scale-110'
                        : 'bg-white/10 hover:bg-white/20'
                    }`}
                    disabled={pageState === 'joining'}
                  >
                    {avatar.emoji}
                  </button>
                ))}
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="p-4 bg-red-500/20 border border-red-500/50 rounded-xl text-red-200 text-center">
                {error}
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={pageState === 'joining' || !gamePin || !nickname.trim()}
              className={`w-full py-4 rounded-xl font-bold text-lg transition-all ${
                pageState === 'joining' || !gamePin || !nickname.trim()
                  ? 'bg-white/20 text-white/50 cursor-not-allowed'
                  : 'bg-white text-purple-700 hover:bg-purple-100 active:scale-98'
              }`}
            >
              {pageState === 'joining' ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  {t('player.join.joining')}
                </span>
              ) : (
                t('player.join.joinButton')
              )}
            </button>
          </form>

          {/* Home Link */}
          <div className="mt-8 text-center">
            <button
              onClick={() => navigate('/')}
              className="text-purple-300 hover:text-white text-sm underline"
            >
              {t('player.join.backToHome')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Kicked state
  if (pageState === 'kicked') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-600 via-red-700 to-red-800 flex items-center justify-center p-4" dir={isRTL ? 'rtl' : 'ltr'}>
        <div className="text-center max-w-md">
          <div className="text-6xl mb-4">😔</div>
          <h2 className="text-2xl font-bold text-white mb-4">{t('player.kicked.title')}</h2>
          <p className="text-red-200 mb-8">{kickedReason}</p>
          <button
            onClick={handleLeave}
            className="px-8 py-3 bg-white text-red-700 font-bold rounded-xl hover:bg-red-100 transition-colors"
          >
            {t('player.kicked.joinAnother')}
          </button>
        </div>
      </div>
    );
  }

  // Playing state
  if (pageState === 'playing' && sessionId && participantId) {
    return (
      <PlayerView
        sessionId={sessionId}
        participantId={participantId}
        onKicked={handleKicked}
        onLeave={handleLeave}
      />
    );
  }

  // Error fallback
  return (
    <div className="min-h-screen bg-gradient-to-br from-red-600 via-red-700 to-red-800 flex items-center justify-center p-4" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="text-center max-w-md">
        <div className="text-6xl mb-4">🤔</div>
        <h2 className="text-2xl font-bold text-white mb-4">{t('player.error.title')}</h2>
        <button
          onClick={handleLeave}
          className="px-8 py-3 bg-white text-red-700 font-bold rounded-xl hover:bg-red-100 transition-colors"
        >
          {t('player.error.tryAgain')}
        </button>
      </div>
    </div>
  );
}

export default LiveQuizPlayPage;
